import * as signalR from '@microsoft/signalr';
import { notification } from 'antd';
import store from '../redux/store';
import i18n from '../../i18n';
import {
  manufacturingOrderUpdateSuccess,
  manufacturingOrderUpdateFailed,
} from '../redux/manufacturingOrder.slice';

import {
  orderUpdateSuccess,
  orderUpdateFailed,
  orderGenerateSuccess,
  orderGenerateFailed,
  fetchOrderById,
} from '../redux/order.slice';

import {
  openInventorySocketSuccess,
  openInventorySocketFailed,
  closeInventorySocketSuccess,
  closeInventorySocketFailed,
} from '../redux/inventory.slice';

import {
  saveInventoryRealQuantitiesSocketSuccess,
  saveInventoryRealQuantitiesSocketFailed,
  fetchStocksByInvnetoryId,
} from '../redux/stock.slice';

class SocketService {
  constructor() {
    this.connection = null;
    this.connected = false;
    this.currentUser = null;
    this.currentShopId = null;
    this.isConnecting = false;
  }

  setUserContext(user) {
    // console.log('[Socket ERP] setUserContext user:', user?.id, 'roles:', user?.roles);
    this.currentUser = user;
  }

  async connect(shopId) {
    if (this.isConnecting) return;

    if (this.connection && this.connected && this.currentShopId === shopId) {
      return;
    }

    if (this.connection) {
      await this.disconnect();
    }

    this.currentShopId = shopId;
    this.isConnecting = true;

    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || 'http://localhost:5005/saleHub';

    const getAccessToken = () =>
      localStorage.getItem('accessToken') ||
      store.getState().auth?.accessToken ||
      '';
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(socketUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: getAccessToken,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.previousRetryCount >= 5) return null;
          return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 16000);
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.serverTimeoutInMilliseconds = 60000;
    this.connection.keepAliveIntervalInMilliseconds = 15000;

    this.connection.onclose((error) => {
      console.log('[Socket ERP] onclose, error =', error?.message || 'No error');
      this.connected = false;
    });

    this.connection.onreconnecting((error) => {
      console.log('[Socket ERP] onreconnecting, error =', error?.message || '');
      this.connected = false;
    });

    this.connection.onreconnected(async (/**connectionId**/) => {
      // console.log('[Socket ERP] onreconnected, connectionId =', connectionId);
      this.connected = true;

      if (
        this.currentShopId &&
        this.connection.state === signalR.HubConnectionState.Connected
      ) {
        try {
          await this.connection.invoke('JoinShopRoom', this.currentShopId);
          // console.log('[Socket ERP] Rejoined shop room:', this.currentShopId);
        } catch (err) {
          console.error('[Socket ERP] Failed to rejoin shop room:', err);
        }
      }
    });

    this.setupListeners();

    try {
      await this.connection.start();
      // console.log('[Socket ERP] Connected, connectionId:', this.connection.connectionId);
      this.connected = true;
      this.isConnecting = false;

      if (shopId && this.connection.state === signalR.HubConnectionState.Connected) {
        try {
          await this.connection.invoke('JoinShopRoom', shopId);
          // console.log('[Socket ERP] Joined shop room:', shopId);
        } catch (err) {
          console.error('[Socket ERP] Failed to join shop room:', err);
        }
      }
    } catch (error) {
      console.error('[Socket ERP] Connection failed:', error);
      this.connected = false;
      this.isConnecting = false;
    }
  }

  joinShopRoom(shopId) {
    if (
      this.connection &&
      this.connection.state === signalR.HubConnectionState.Connected
    ) {
      this.connection
        .invoke('JoinShopRoom', shopId)
        .then(() => console.log('[Socket ERP] Joined shop room (manual):', shopId))
        .catch((err) =>
          console.error('[Socket ERP] Failed to join shop room:', err)
        );
    }
  }

  setupListeners() {
    if (!this.connection) return;

    this.connection.off('JoinedShopRoom');
    this.connection.off('ManufacturingOrderUpdateCompleted');
    this.connection.off('OrderUpdateCompleted');
    this.connection.off('GenerateShopOrderTomorrowCompleted');
    this.connection.off('GenerateShopOrderByDateCompleted');
    this.connection.off('OpenInventoryCompleted');
    this.connection.off('CloseInventoryCompleted');
    this.connection.off('UpdateInventoryRealQuantitiesCompleted');

    this.connection.on('JoinedShopRoom', (/**message**/) => {
      // // console.log('[Socket ERP Event] JoinedShopRoom:', message);
    });

    this.connection.on('ManufacturingOrderUpdateCompleted', (payload) => {
      //  // console.log('[Socket ERP Event] ManufacturingOrderUpdateCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          manufacturingOrderUpdateSuccess({
            jobId: payload.jobId,
            orderId: payload.orderId,
            result: payload.result,
          })
        );

        notification.success({
          message: i18n.t('socket.updateCompleted'),
          description: i18n.t(
            'socket.manufacturingOrderUpdateSuccess'
          ), placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          manufacturingOrderUpdateFailed({
            jobId: payload?.jobId,
            orderId: payload?.orderId,
            error: payload?.errorMessage,
          })
        );

        notification.error({
          message: i18n.t('socket.updateFailed'),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('OrderUpdateCompleted', async (payload) => {
      //  // console.log('[Socket ERP Event] OrderUpdateCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          orderUpdateSuccess({
            jobId: payload.jobId,
            orderId: payload.orderId,
            result: payload.result,
          })
        );

        if (payload?.orderId) {
          store.dispatch(fetchOrderById(payload.orderId));
        }

        notification.success({
          message: i18n.t('socket.updateCompleted'),
          description: i18n.t(
            'socket.orderUpdateSuccess'
          ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          orderUpdateFailed({
            jobId: payload?.jobId,
            orderId: payload?.orderId,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t('socket.updateFailed'),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('GenerateShopOrderTomorrowCompleted', (payload) => {
      //  // console.log('[Socket ERP Event] GenerateShopOrderTomorrowCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          orderGenerateSuccess({
            jobId: payload.jobId,
            mode: 'generateTomorrow',
            shopId: payload.shopId,
            result: payload.result,
          })
        );

        const orders = Array.isArray(payload?.result)
          ? payload.result
          : payload?.result
            ? [payload.result]
            : [];

        const orderNumbers = orders
          .map((x) => x?.orderNumber || x?.order_number)
          .filter(Boolean);

        notification.success({
          message: i18n.t(
            'socket.generationCompleted'
          ),
          description:
            orderNumbers.length > 0
              ? i18n.t('socket.ordersGenerated', {
                orderNumbers:
                  orderNumbers.join(', '),
              })
              : i18n.t(
                'socket.tomorrowShopOrdersGenerated'
              ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          orderGenerateFailed({
            jobId: payload?.jobId,
            mode: 'generateTomorrow',
            shopId: payload?.shopId,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t(
            'socket.generationFailed'
          ),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('GenerateShopOrderByDateCompleted', (payload) => {
      //  // console.log('[Socket ERP Event] GenerateShopOrderByDateCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          orderGenerateSuccess({
            jobId: payload.jobId,
            mode: 'generateByDate',
            shopId: payload.shopId,
            orderDate: payload.orderDate,
            result: payload.result,
          })
        );

        const orderNumber =
          payload?.result?.orderNumber ||
          payload?.result?.order_number ||
          payload?.result?.order?.orderNumber ||
          payload?.result?.order?.order_number;

        notification.success({
          message: i18n.t(
            'socket.generationCompleted'
          ),
          description: orderNumber
            ? i18n.t(
              'socket.shopOrderGenerated',
              {
                orderNumber,
              }
            )
            : i18n.t(
              'socket.shopOrderGeneratedWithoutNumber'
            ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          orderGenerateFailed({
            jobId: payload?.jobId,
            mode: 'generateByDate',
            shopId: payload?.shopId,
            orderDate: payload?.orderDate,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t(
            'socket.generationFailed'
          ),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('OpenInventoryCompleted', (payload) => {
      // console.log('[Socket ERP Event] OpenInventoryCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          openInventorySocketSuccess({
            jobId: payload.jobId,
            inventoryId: payload.inventoryId,
          })
        );

        notification.success({
          message: i18n.t(
            'socket.openingCompleted'
          ),
          description: i18n.t(
            'socket.inventoryOpened'
          ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          openInventorySocketFailed({
            jobId: payload?.jobId,
            inventoryId: payload?.inventoryId,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t(
            'socket.openingFailed'
          ),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('CloseInventoryCompleted', (payload) => {
      // console.log('[Socket ERP Event] CloseInventoryCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          closeInventorySocketSuccess({
            jobId: payload.jobId,
            inventoryId: payload.inventoryId,
          })
        );

        if (payload?.inventoryId) {
          store.dispatch(fetchStocksByInvnetoryId(payload.inventoryId));
        }

        notification.success({
          message: i18n.t(
            'socket.closingCompleted'
          ),
          description: i18n.t(
            'socket.inventoryClosed'
          ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          closeInventorySocketFailed({
            jobId: payload?.jobId,
            inventoryId: payload?.inventoryId,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t(
            'socket.closingFailed'
          ),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });

    this.connection.on('UpdateInventoryRealQuantitiesCompleted', (payload) => {
      // console.log('[Socket ERP Event] UpdateInventoryRealQuantitiesCompleted:', payload);

      if (payload?.success) {
        store.dispatch(
          saveInventoryRealQuantitiesSocketSuccess({
            jobId: payload.jobId,
            inventoryId: payload.inventoryId,
            result: payload.result,
          })
        );

        if (payload?.inventoryId) {
          store.dispatch(fetchStocksByInvnetoryId(payload.inventoryId));
        }

        notification.success({
          message: i18n.t(
            'socket.savingCompleted'
          ),
          description: i18n.t(
            'socket.realQuantitiesSaved'
          ),
          placement: 'topRight',
          duration: 0,
        });
      } else {
        store.dispatch(
          saveInventoryRealQuantitiesSocketFailed({
            jobId: payload?.jobId,
            inventoryId: payload?.inventoryId,
            error:
              payload?.errorMessage ||
              i18n.t('socket.errorOccurred'),
          })
        );

        notification.error({
          message: i18n.t(
            'socket.savingFailed'
          ),
          description:
            payload?.errorMessage ||
            i18n.t('socket.errorOccurred'),
          placement: 'topRight',
          duration: 0,
        });
      }
    });
  }

  on(event, callback) {
    if (this.connection) {
      this.connection.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.connection) {
      this.connection.off(event, callback);
    }
  }

  async disconnect() {
    if (this.connection) {
      this.isConnecting = false;
      // console.log('[Socket ERP] Disconnecting...');
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[Socket ERP] Error during disconnect:', error);
      } finally {
        this.connection = null;
        this.connected = false;
        this.currentShopId = null;
      }
    }
  }

  isConnected() {
    const state = this.connection?.state;
    return this.connected && state === signalR.HubConnectionState.Connected;
  }

  getConnection() {
    return this.connection;
  }
}

export const socketService = new SocketService();